import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { CalendarIcon, XIcon } from './Icons';

const DateInput = ({
  value,
  onChange,
  name,
  placeholder = "Select date",
  required = false,
  readOnly = false,
  disabled = false,
  className,
  minDate,
  maxDate,
  showClearButton = true,
  dateFormat = "yyyy-MM-dd",
  showFormatHint = true,
  ...props
}) => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      // Format as DD/MM/YYYY for better UX
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format date for input value (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Handle date change
  const handleDateChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: newValue,
          type: 'date'
        }
      });
    }
  };

  // Handle clear button
  const handleClear = (event) => {
    event.stopPropagation();
    setInputValue('');
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
          type: 'date'
        }
      });
    }
  };

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2">
        {/* Native date input (hidden but functional) */}
        <input
          ref={inputRef}
          type="date"
          name={name}
          value={formatDateForInput(inputValue)}
          onChange={handleDateChange}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          min={minDate}
          max={maxDate}
          className="absolute opacity-0 pointer-events-none"
          {...props}
        />
        
        {/* Custom styled input */}
        <div
          className={cn(
            `flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md transition-colors cursor-pointer`,
            `${theme.colors.surface} ${theme.text.primary} ${theme.border.light}`,
            `hover:${theme.border.default} focus-within:${theme.border.focus} focus-within:ring-2 focus-within:ring-blue-500/20`,
            disabled && 'opacity-50 cursor-not-allowed',
            readOnly && 'cursor-default',
            className
          )}
          onClick={() => !disabled && !readOnly && inputRef.current?.showPicker?.()}
        >
          <span className={inputValue ? theme.text.primary : theme.text.muted}>
            {inputValue ? formatDateForDisplay(inputValue) : placeholder}
          </span>
          
          {/* Calendar icon */}
          <CalendarIcon className={`w-4 h-4 ${theme.text.muted} flex-shrink-0 ml-2`} />
        </div>
        
        {/* Clear button - outside the input */}
        {showClearButton && (value || inputValue) && !disabled && !readOnly && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'flex h-[40px] min-w-[40px] items-center justify-center rounded-md border p-2 transition-colors',
              'border-gray-200 bg-white/80 text-gray-500 shadow-sm backdrop-blur-sm',
              'hover:border-red-200 hover:bg-red-50 hover:text-red-600',
              'dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300',
              'dark:hover:border-red-800 dark:hover:bg-red-950/20 dark:hover:text-red-400',
              'focus:outline-none focus:ring-2 focus:ring-red-500/30'
            )}
            title="Clear date"
            aria-label="Clear date"
          >
            <XIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {/* Helper text for date format */}
      {showFormatHint && !readOnly && (
        <div className={`mt-1 text-xs ${theme.text.muted}`}>
          Format: DD/MM/YYYY
        </div>
      )}
    </div>
  );
};

export default DateInput;

